import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { TipsOutline } from '@uplift/icons';

import whatIsTalkTherapySvg from '../../assets/what-is-talk-therapy.svg';
import {
  StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepTipPopover,
  StepToggleButton, StepToggleButtonGroup
} from '../../components';
import { LevelOfCareNextData } from '../types';
import { CareType } from '@uplift/api';

export type LevelOfCareProps = {
  onNext: (data: LevelOfCareNextData) => void;
  onBack: () => void;
  isBookingForSelf: boolean;
};

export const LevelOfCare: React.VFC<LevelOfCareProps> = props => {
  const { classes, cx } = useLevelOfCareStyles();

  return (
    <StepContainer>
      <StepContent>
        <StepHeading>{props.isBookingForSelf ? 'What type of care are you seeking?' : 'What type of care are they seeking?'}</StepHeading>
        <StepForm maxWidth="343px">
          <StepToggleButtonGroup onChange={(_, careType: CareType | null) => props.onNext({ careType })}>
            <StepToggleButton value={CareType.therapy}>Talk Therapy</StepToggleButton>
            <StepToggleButton value={CareType.psychiatry}>Medication Management</StepToggleButton>
            <StepToggleButton value={CareType.therapyAndPsychiatry}>Both</StepToggleButton>
            <StepToggleButton value={null}>I’m not sure</StepToggleButton>
          </StepToggleButtonGroup>
        </StepForm>
      </StepContent>
      <StepTipPopover title="What’s right for me?" classes={{ dialogContent: classes.tipDialogContent }}>
        <div className={classes.tipSection}>
          <img
            src={whatIsTalkTherapySvg}
            alt="what’s right for me?"
          />
          <Typography variant="h3" className={classes.mt_3}>When to seek talk therapy</Typography>
          <div className={cx(classes.atItsCore, classes.mt_3)}>
            <Typography variant="overline">Therapy at it’s core</Typography>
            <Typography>
              Talk through issues and concerns to help you understand yourself
            </Typography>
          </div>
          <Typography className={classes.mt_3}>
            Talk therapy creates a path for change. If you’re looking to understand and unpack your thoughts, experiences, and patterns, therapy can provide perspective, skills, and healthy habits.
          </Typography>
          <Typography className={classes.mt_2}>
            Therapy is a supportive space to work through challenges—on your own or together with loved ones, like in couples or family counseling.
          </Typography>
          <Typography className={cx(classes.fontWeight600, classes.mt_2)}>
            Some reasons to start therapy
          </Typography>
          <ul className={classes.list}>
            <Typography component="li">Life transitions</Typography>
            <Typography component="li">Relationship issues</Typography>
            <Typography component="li">Stress management</Typography>
            <Typography component="li">Grief and loss</Typography>
            <Typography component="li">Breaking unhealthy patterns</Typography>
            <Typography component="li">Setting goals–and reaching them</Typography>
          </ul>
        </div>
        <div className={classes.tipSection}>
          <img
            src={whatIsTalkTherapySvg}
            alt="what’s right for me?"
          />
          <Typography variant="h3" className={classes.mt_3}>When to seek medication management</Typography>
          <div className={cx(classes.atItsCore, classes.mt_3)}>
            <Typography variant="overline">Medication management at it’s core</Typography>
            <Typography>
              Diagnose and prescribe medication to treat mental health disorders
            </Typography>
          </div>
          <Typography className={classes.mt_3}>
            If you’re having a hard time functioning in everyday life, you might consider medication as part of your mental health treatment.
          </Typography>
          <Typography className={classes.mt_2}>
            A consultation can help you figure out if you should try medication, talk therapy, or both.
          </Typography>
          <Typography className={cx(classes.fontWeight600, classes.mt_2)}>
            Some reasons to consider a consultation
          </Typography>
          <ul className={classes.list}>
            <Typography component="li">Difficulty sleeping</Typography>
            <Typography component="li">Struggling to do any work or responsibilities</Typography>
            <Typography component="li">Constant feelings of hopelessness</Typography>
            <Typography component="li">Persistent worry</Typography>
            <Typography component="li">Severe lack of energy</Typography>
          </ul>
        </div>
        <div className={classes.tipFooter}>
          <div className={classes.tipFooterIconContainer}>
            <TipsOutline />
          </div>
          <Typography className={classes.fontWeight600}>
            Ways to learn if medication management is right for you:
          </Typography>
          <ul className={classes.list}>
            <Typography component="li">Select <span className={classes.fontWeight600}>“I’m not sure”</span> to answer a few questions</Typography>
            <Typography component="li">Ask your therapist, if you have one</Typography>
            <Typography component="li">Schedule a psychiatric consultation</Typography>
          </ul>
        </div>
      </StepTipPopover>
      <StepNavigator onBack={props.onBack} desktopOnly />
    </StepContainer>
  );
};

const useLevelOfCareStyles = makeStyles({ name: 'LevelOfCare' })(theme => ({
  tipDialogContent: {
    padding: theme.spacing(2, 0)
  },
  tipSection: {
    padding: theme.spacing(0, 2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 4)
    },
    '&:not(:first-of-type)': {
      marginTop: theme.spacing(10),
      [theme.breakpoints.up('sm')]: {
        marginTop: theme.spacing(6),
      }
    }
  },
  atItsCore: {
    background: theme.palette.secondary.contrastText,
    padding: theme.spacing(2, 3),
    marginTop: theme.spacing(3),
  },
  mt_2: {
    marginTop: theme.spacing(2)
  },
  mt_3: {
    marginTop: theme.spacing(3)
  },
  fontWeight600: {
    fontWeight: 600
  },
  list: {
    paddingInlineStart: theme.spacing(3),
    margin: 0,
    marginTop: theme.spacing(2)
  },
  tipFooter: {
    marginTop: theme.spacing(5),
    background: theme.palette.info.dark,
    color: theme.palette.secondary.contrastText,
    padding: theme.spacing(4, 3, 3),
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(6, 4, 4),
    }
  },
  tipFooterIconContainer: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: '48px',
    width: '48px',
    background: theme.palette.info.background,
    border: `1px solid ${theme.palette.info.dark}`,
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    '& > svg': {
      color: theme.palette.info.main,
    }
  }
}));
